/**
 * Continuously scrolling carousel that stays draggable.
 *
 * The marquee could be a pure CSS transform animation, but a transform cannot
 * be grabbed: the element never actually scrolls, so touch swipe and mouse drag
 * have nothing to act on. This drives `scrollLeft` instead, which keeps native
 * touch scrolling and momentum for free and leaves the position writable when a
 * pointer takes over.
 *
 * The track holds the cards twice. Once the scroll passes the width of one set
 * the position jumps back by exactly that width - the pixels under the viewport
 * are identical either side of the jump, so the loop is invisible.
 */
class CarouselMarquee extends HTMLElement {
  #raf = null;
  #position = 0;
  #lastWritten = 0;
  #lastFrame = 0;
  #pointerPaused = false;
  #hidden = false;
  #offscreen = true;
  #dragging = false;
  #dragMoved = false;
  #observer = null;

  connectedCallback() {
    this.track = this.querySelector('.carousel-marquee__track');
    if (!this.track) return;

    this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    this.#position = this.scrollLeft;

    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointerenter', this.#pause);
    this.addEventListener('pointerleave', this.#resume);
    this.addEventListener('focusin', this.#pause);
    this.addEventListener('focusout', this.#resume);
    this.addEventListener('click', this.#handleClick, { capture: true });
    document.addEventListener('visibilitychange', this.#handleVisibility);

    // Off-screen marquees burn frames for nothing.
    this.#observer = new IntersectionObserver(
      ([entry]) => {
        this.#offscreen = !entry.isIntersecting;
      },
      { rootMargin: '100px' }
    );
    this.#observer.observe(this);

    this.#raf = requestAnimationFrame(this.#step);
  }

  disconnectedCallback() {
    if (this.#raf) cancelAnimationFrame(this.#raf);
    this.#observer?.disconnect();
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  /** Distance between a card and its clone: one full set plus one gap. */
  get #loopDistance() {
    const cards = this.track.querySelectorAll('.card');
    const count = parseInt(this.dataset.cards ?? '0', 10);

    if (!count || cards.length < count + 1) return 0;

    return cards[count].offsetLeft - cards[0].offsetLeft;
  }

  /** Pixels per second, derived from the seconds-per-card setting. */
  get #speed() {
    const seconds = parseFloat(this.dataset.speed ?? '0');
    const card = this.track.querySelector('.card');

    if (!seconds || !card) return 0;

    return (card.offsetWidth + this.#gap) / seconds;
  }

  get #gap() {
    return parseFloat(getComputedStyle(this.track).columnGap) || 0;
  }

  get #paused() {
    return this.#pointerPaused || this.#dragging || this.#hidden || this.#offscreen || this.reducedMotion.matches;
  }

  #step = (now) => {
    const elapsed = this.#lastFrame ? (now - this.#lastFrame) / 1000 : 0;
    this.#lastFrame = now;

    // A pointer drag or a native touch scroll moves the element behind our
    // back, so the position is re-read whenever it differs from what we wrote.
    if (Math.abs(this.scrollLeft - this.#lastWritten) > 1) this.#position = this.scrollLeft;

    // Clamped: a backgrounded tab resumes with a huge elapsed time, which would
    // otherwise fling the carousel forward on return.
    if (!this.#paused) this.#position += this.#speed * Math.min(elapsed, 0.1);

    this.#normalize();
    this.scrollLeft = this.#position;
    this.#lastWritten = this.scrollLeft;
    this.#raf = requestAnimationFrame(this.#step);
  };

  /** Keeps the scroll position inside the first set of cards. */
  #normalize() {
    const loop = this.#loopDistance;
    if (loop <= 0) return;

    if (this.#position >= loop) this.#position -= loop;
    else if (this.#position < 0) this.#position += loop;
  }

  #pause = () => {
    this.#pointerPaused = true;
  };

  #resume = () => {
    this.#pointerPaused = false;
  };

  #handleVisibility = () => {
    this.#hidden = document.hidden;
  };

  /**
   * Mouse only. Touch is left to the browser so swipes keep their momentum and
   * the page can still be scrolled vertically from on top of the carousel.
   */
  #handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;

    this.#dragging = true;
    this.#dragMoved = false;
    this.setPointerCapture(event.pointerId);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);
  };

  #handlePointerMove = (event) => {
    if (!this.#dragging) return;

    if (Math.abs(event.movementX) > 0) this.#dragMoved = true;
    this.#position -= event.movementX;
    this.#normalize();
    this.scrollLeft = this.#position;
    this.#lastWritten = this.scrollLeft;
  };

  #handlePointerUp = (event) => {
    this.#dragging = false;
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerUp);
  };

  /** Stops a drag that ends on a link from counting as a click. */
  #handleClick = (event) => {
    if (!this.#dragMoved) return;

    event.preventDefault();
    event.stopPropagation();
    this.#dragMoved = false;
  };
}

if (!customElements.get('carousel-marquee')) {
  customElements.define('carousel-marquee', CarouselMarquee);
}
